"use client";

/**
 * AlertCellModalContext — shared provider + hook for the AlertCellModal.
 *
 *  - `regenerate` flow is removed. The modal performs a single fetch with one
 *    silent retry on network failure; after that it renders a compact
 *    "Couldn't generate explanation right now." message and gives up.
 *  - Cached summary markdown is kept per `cellIndex` in a Map on the provider
 *    so reopening the same node is instant.
 *  - `outputs` is the raw notebook cell output array — passed straight through
 *    to AlertCellOutputsView which mirrors NotebookCell's renderers (charts,
 *    unified search, fund/sub agent carousels, text).
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

/**
 * Matches the `{type, data}` shape NotebookCell consumes. Keeping it loose
 * with `any` on `data` so we can pipe through every specialized renderer
 * (plotly, backtest-tabs, dashboard, unified_search, fund_agent, sub_agent,
 * html, image, error, text) without narrowing.
 */
export interface AlertCellOutput {
  type:
    | "text"
    | "image"
    | "error"
    | "html"
    | "plotly"
    | "bokeh"
    | "unified_search"
    | "dashboard"
    | "backtest-tabs"
    | "fund_agent"
    | "sub_agent"
    | string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
}

export interface AlertCellModalData {
  cellIndex: number;
  displayIndex: number;
  purpose: string;
  code: string;
  /** Legacy concatenated stdout — retained for fallback when `outputs` is empty. */
  stdout: string;
  status: "idle" | "pending" | "running" | "success" | "error";
  hasAlertSend: boolean;
  /** Raw notebook cell outputs (same shape NotebookCell receives). */
  outputs?: AlertCellOutput[];
  /** Session id so the modal can call /alerts/explain-cell. */
  sessionId?: string;
  /** Which outer modal tab to open on. Defaults to "summary". Used by the
   *  hero (last) node on /alerts/[id] which passes "output" so the user
   *  lands directly on the dashboard view — AlertCellOutputsView picks
   *  "dashboard" as its first inner tab when a dashboard output exists. */
  initialTab?: "summary" | "code" | "output";
  /** Pre-generated rich markdown description for this cell. When provided,
   *  the modal's Summary tab skips the /alerts/explain-cell fetch entirely
   *  (no loader, no LLM call) — the markdown is already the exact same
   *  content the canvas card is showing, pre-generated at alert upload
   *  time and stored on `alertDoc.cell_descriptions`. Null for legacy
   *  alerts mid-backfill; the modal falls back to the fetch path. */
  descriptionMd?: string | null;
}

interface AlertCellModalContextType {
  isOpen: boolean;
  data: AlertCellModalData | null;
  openModal: (d: AlertCellModalData) => void;
  closeModal: () => void;
  getCachedExplanation: (cellIndex: number) => string | undefined;
  setCachedExplanation: (cellIndex: number, markdown: string) => void;
}

const Ctx = createContext<AlertCellModalContextType | null>(null);

export function AlertCellModalProvider({ children }: { children: ReactNode }) {
  const [isOpen, setOpen] = useState(false);
  const [data, setData] = useState<AlertCellModalData | null>(null);
  const explanationCacheRef = useRef<Map<number, string>>(new Map());

  const openModal = useCallback((d: AlertCellModalData) => {
    setData(d);
    setOpen(true);
  }, []);
  const closeModal = useCallback(() => {
    setOpen(false);
    setTimeout(() => setData(null), 200);
  }, []);

  const getCachedExplanation = useCallback(
    (cellIndex: number) => explanationCacheRef.current.get(cellIndex),
    [],
  );
  const setCachedExplanation = useCallback(
    (cellIndex: number, markdown: string) => {
      explanationCacheRef.current.set(cellIndex, markdown);
    },
    [],
  );

  const value = useMemo<AlertCellModalContextType>(
    () => ({
      isOpen,
      data,
      openModal,
      closeModal,
      getCachedExplanation,
      setCachedExplanation,
    }),
    [isOpen, data, openModal, closeModal, getCachedExplanation, setCachedExplanation],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAlertCellModal() {
  const ctx = useContext(Ctx);
  if (!ctx)
    throw new Error(
      "useAlertCellModal must be used within <AlertCellModalProvider>",
    );
  return ctx;
}
